/**
 * A view as somebody with no account here sees it.
 *
 * Every endpoint is addressed by the view's slug rather than its id, and none
 * of them needs a session. What a reader may see is decided entirely on the
 * server: the fields the view hides are absent from the response, and its
 * filters are applied without being disclosed -- `PublicView` carries
 * `sortings` and `group_bys` and deliberately no `filters`, because a filter
 * can name a value in a column the reader is not being shown.
 */
import { queryOptions, useQuery } from '@tanstack/react-query'

import { getApi, unwrap } from '@/api/client'
import { qk } from '@/api/query-keys'
import type { Field } from '@/api/types'
import type { GridFieldOptions } from '../grid/columns'

/** The view and the columns of it a reader may see. */
export interface PublicViewInfo {
  view: {
    /** The slug, which is what the serialiser reports as the id. */
    id: string
    name: string
    type: string
    slug: string
    show_logo?: boolean
    table: { id: number; name?: string; database_id?: number }
    sortings?: unknown[]
    group_bys?: unknown[]
  }
  fields: Field[]
}

/**
 * What this view is.
 *
 * Not retried, because every way this fails is an answer rather than an
 * accident: the link is dead, the view has been unshared, or it is one of the
 * few still holding a password from a version of this application that could
 * set one -- and none of those changes on a second attempt.
 */
export function publicViewQuery(slug: string) {
  return queryOptions({
    queryKey: qk.publicView(slug),
    retry: false,
    meta: { skipGlobalErrorHandler: true },
    queryFn: async (): Promise<PublicViewInfo> => {
      // Cast at the boundary, like a row payload and for the same reason: the
      // endpoint serialises each field through the field type registry, so the
      // type-specific properties are all there, and `PublicField` in the
      // contract is the shape drf-spectacular could describe rather than the
      // shape that arrives.
      const body = await unwrap(
        getApi().GET('/api/database/views/{slug}/public/info/', {
          params: { path: { slug } },
        }),
      )
      return body as unknown as PublicViewInfo
    },
  })
}

export function usePublicView(slug: string) {
  return useQuery(publicViewQuery(slug))
}

/**
 * How wide each column is, and in what order.
 *
 * Asked of the rows endpoint with `limit=1`, because that is the only endpoint
 * that answers with a public view's field options -- there is no endpoint of
 * their own, deliberately: the options are part of what the view looks like and
 * a reader cannot change them. One row is fetched and thrown away, which is the
 * price of not inventing an endpoint; the alternative is every public grid
 * showing every column at the default width.
 */
export function publicFieldOptionsQuery(slug: string) {
  return queryOptions({
    queryKey: [...qk.publicView(slug), 'fieldOptions'] as const,
    retry: false,
    meta: { skipGlobalErrorHandler: true },
    queryFn: async (): Promise<Record<string, GridFieldOptions>> => {
      const body = await unwrap<{
        field_options?: Record<string, GridFieldOptions>
      }>(
        getApi().GET('/api/database/views/grid/{slug}/public/rows/', {
          params: {
            path: { slug },
            query: { limit: 1, exclude_count: true, include: 'field_options' },
          },
        }),
      )
      return body.field_options ?? {}
    },
  })
}

export function usePublicFieldOptions(slug: string) {
  return useQuery(publicFieldOptionsQuery(slug))
}
